import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import PulsoidClient, {
  DEFAULT_RETRY_OPTIONS,
  GetCheckoutProductsResponse,
} from '../../PulsoidClient';
import {PricingBlock} from '../../system/PricingBlock/PricingBlock';
import css from './CouponsCheckout.module.css';
import {COUPON_ROUTES} from '../navigation/coupon/CouponRoutes';
import {LoaderOverlay} from '../../system/Loader/Loader';
import {Action} from '../../system/buttons';
import {CreateCheckoutSessionErrorModal} from './errorModals/CreateCheckoutSessionErrorModal';
import {GetPurchaseCouponErrorModal} from './errorModals/GetPurchaseCouponErrorModal';

export const CouponsCheckout = () => {
  const [checkoutInfo, setCheckoutInfo] =
    useState<GetCheckoutProductsResponse>();
  const [pollingInProgress, setPollingInProgress] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [showCheckoutSessionError, setShowCheckoutSessionError] =
    useState(false);
  const [showGetPurchaseCouponError, setShowGetPurchaseCouponError] =
    useState(false);
  const [checkoutSessionId, setCheckoutSessionId] = useState('');

  const history = useHistory();
  const publicClient = useRef(
    new PulsoidClient({
      publicRpc: true,
      retryOptions: DEFAULT_RETRY_OPTIONS,
    })
  ).current;

  const mainContainerRef = useRef<HTMLDivElement>(null);

  const fetchUserInfo = useCallback(async () => {
    try {
      const userInfo = await new PulsoidClient({
        noAuthRedirect: true,
      }).getUserInfo();
      setUserEmail(userInfo?.email);
    } catch (e) {
      // Ignore
    }
  }, []);
  const fetchCheckoutInfo = useCallback(async () => {
    try {
      const response = await publicClient.getCouponCheckoutProducts();
      setCheckoutInfo(response);
      return response;
    } catch (e) {
      // TODO: Clarify how to handle such errros
      console.error(e);
    }
  }, []);

  const initialize = useCallback(async () => {
    const [info] = await Promise.all([fetchCheckoutInfo(), fetchUserInfo()]);

    if (info) {
      if (info?.sandbox) {
        Paddle?.Environment?.set('sandbox');
      }
      Paddle?.Setup?.({
        vendor: Number(info?.providerVendorId),
      });
    }
  }, []);

  const startPolling = async (sessionId: string, attempt = 1) => {
    if (sessionId) {
      setPollingInProgress(true);
      try {
        const response = await publicClient.getPurchasedCoupon({
          checkoutSessionId: sessionId,
        });

        if (!response?.coupon) {
          if (attempt < 20) {
            setTimeout(() => startPolling(sessionId, attempt + 1), 1000);
          } else {
            throw new Error('To many retry attempts');
          }
        } else {
          setPollingInProgress(false);

          const searchParams = new URLSearchParams();
          searchParams.append('coupon', response.coupon);
          response.duration &&
            searchParams.append('duration', response.duration);

          history.push(`${COUPON_ROUTES.COUPONS_THANK_YOU}?${searchParams}`);
        }
      } catch (e) {
        setPollingInProgress(false);
        setShowGetPurchaseCouponError(true);
        setCheckoutSessionId('');
        console.error('Unable to get purchased coupon', e);
      }
    }
  };

  const onBuyClick = useCallback(
    (productId: string, providerProductId: string) => async () => {
      try {
        const {checkoutSessionId: sessionId} =
          await publicClient.createCouponCheckoutSession({
            productId: productId,
          });

        setCheckoutSessionId(sessionId);

        Paddle.Checkout.open({
          product: providerProductId,
          email: userEmail,
          // override: product.provider.override(),
          passthrough: sessionId,
          displayModeTheme: 'dark',
          successCallback: () => startPolling(sessionId),
          allowQuantity: false,
        });
      } catch (e) {
        console.error(
          'Unable to create checkout session and initialize paddle',
          e
        );
        setShowCheckoutSessionError(true);
      }
    },
    [userEmail, checkoutSessionId]
  );

  useEffect(() => {
    initialize();
  }, []);

  const products = checkoutInfo?.products || [null, null, null];

  return (
    <>
      {pollingInProgress && (
        <LoaderOverlay
          backgroundColor="#000000c0"
          color="#ffffff"
          title="Wrapping up your coupon"
        />
      )}

      <CreateCheckoutSessionErrorModal
        isOpen={showCheckoutSessionError}
        onClose={() => setShowCheckoutSessionError(false)}
      />

      <GetPurchaseCouponErrorModal
        isOpen={showGetPurchaseCouponError}
        onClose={() => setShowGetPurchaseCouponError(false)}
        checkoutSessionId={checkoutSessionId}
      />

      <main className={css.root}>
        <div className={css.introBlock}>
          <div className={css.introTitleContainer}>
            <img
              className={css.introImage}
              src="https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content%2Fcoupon-purchase%2Fpricing-2-mascots.png"
            />
            <h1 className={css.introTitle}>
              <div className={`${css.introTitleLine} ${css.titleFirstLine}`}>
                power up
              </div>
              <div className={`${css.introTitleLine} ${css.titleSecondLine}`}>
                your BRO with
              </div>
              <div className={css.introTitleLine}>
                <span className={css.pulsoidFont}>pulsoid</span>
                <div className={css.broPlanLabel}>
                  <span>bro</span>
                  <span>plan</span>
                </div>
              </div>
            </h1>
          </div>

          <div className={css.introDescription}>
            <p>
              Wanna give a little something to your favorite streamer to amplify
              their content?
              <br />
              Can’t think of a useful gift for your friend who streams or makes
              videos?
              <br />
              Need to organize a giveaway for content creators?
              <br />
              Now you can buy Pulsoid BRO Plan coupons to do all that!
            </p>
          </div>
        </div>

        <div className={css.mainContainer} ref={mainContainerRef}>
          <div className={css.mainDescription}>
            <div className={css.mainDescriptionBlock}>
              <h2 className={css.mainDescriptionTitle}>Integrations</h2>
              <p className={css.mainDescriptionText}>
                Set up automated clips that can be posted on your Discord.
                Connect your heart rate to your RGB lights via Lumia Stream to
                create an ultimate light show. Trigger apps with IFTTT. Make
                your heart rate change your voice with Voicemod. And more!
              </p>
            </div>
            <div className={css.mainDescriptionBlock}>
              <h2 className={css.mainDescriptionTitle}>Advanced Widgets</h2>
              <p className={css.mainDescriptionText}>
                Get full access to more than 40 premium configurable widgets
                with an ever-expanding library that includes game inspired
                styles, BPM ranges, graph, sound, and fitness widgets.
              </p>
            </div>
            <div className={css.mainDescriptionBlock}>
              <h2 className={css.mainDescriptionTitle}>
                Upload Your Own Media
              </h2>
              <p className={css.mainDescriptionText}>
                Take it a step further and create unique widgets with your own
                images, video, and sounds. Create widgets that perfectly amplify
                your brand.
              </p>
            </div>
          </div>

          <div className={css.mainPurchase}>
            {products.map((product, idx) => (
              <PricingBlock
                loading={!product}
                key={product?.id || idx}
                title={product?.title}
                price={product?.price}
                discount={product?.discount}
                buttonLabel="buy coupon"
                onBuyClick={onBuyClick(
                  product?.id,
                  product?.provider?.productId
                )}
              />
            ))}
          </div>
        </div>

        <div className={css.footerContainer}>
          <div className={css.footerDisclaimer}>
            All coupons are valid for a year since the day of purchase
          </div>

          <div>
            <h4 className={css.footerTitle}>
              Making a gift has never been so easy!
            </h4>
            <div className={css.footerSubtitle}>
              Just follow these 3 simple steps:
            </div>
          </div>

          <div className={css.footerPuchaseSteps}>
            <div className={css.puchaseStep}>
              <div className={css.purchaseStepTitle}>
                <span className={css.puchaseStepNumber}>1. </span>
                Purchase a coupon for the duration of your choice
              </div>
              <img
                src="https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content%2Fcoupon-purchase%2Fpurchase-steps-1.png"
                alt="purchase coupon mascot"
                className={css.puchaseStepMascot}
              />
            </div>

            <div className={css.puchaseStep}>
              <div className={css.purchaseStepTitle}>
                <span className={css.puchaseStepNumber}>2. </span>
                Get your coupon from the page that’ll open and via an email
              </div>
              <img
                src="https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content%2Fcoupon-purchase%2Fpurchase-steps-2.png"
                alt="get your coupon mascot"
                className={css.puchaseStepMascot}
              />
            </div>

            <div className={css.puchaseStep}>
              <div className={css.purchaseStepTitle}>
                <span className={css.puchaseStepNumber}>3. </span>
                Share the coupon with your friend or gift it to a member of your
                community
              </div>
              <img
                src="https://pulsoid-magi.nyc3.cdn.digitaloceanspaces.com/pages-content%2Fcoupon-purchase%2Fpurchase-steps-3.png"
                alt="share with friend mascot"
                className={css.puchaseStepMascot}
              />
            </div>
          </div>
        </div>

        <Action
          className={css.buyCouponButton}
          onClick={() =>
            mainContainerRef?.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }
          outline
          square
        >
          pick a coupon
        </Action>
      </main>
    </>
  );
};
